<script lang="ts" setup>   
    import '../../css/choice.css';
    import {reactive,watch,computed} from 'vue';
    import {GetMedWeblink,IMedInformationInput,IRxWebLink,medInformationType,printClass,
            no_drug_information,UpdateWebLink,IWebLinkInputs,removeBadChars,errorReturnMessage} from '../lib/Models/InteractionModel';  
    import ActionButtonV2 from '../components/ActionButtonV2.vue';
    import {ActionButtonStyle} from '../lib/Types/Types';
    import {ButtonDisplaySize,ButtonDisplayStyle} from "../Enums/UiEnums"; 
    import {confirmDialog,popAlert} from '../lib/Notification';
    import { isValidUrl } from '../lib/ChoiceUtils';

    const formData = reactive <
    {   
        medRetrieved: boolean, 
        weblinkList? : IRxWebLink[],
        noData: boolean,
        medErrorMessage? : string 
    }>
    ({     
        medRetrieved : false, 
        noData: true,
        medErrorMessage: "" 
    });
    const props = withDefaults( defineProps<
        {
            showDialog: boolean, 
            patientID: number,
            prescriptionID?: number,
            medicationID?: number, 
            displayHeight? : number
        }>(),
        {
            showDialog: false,
            patientID: 0,
            prescriptionID: 0,
            medicationID: 0,
            displayHeight : 350
        });

    watch(() => props.showDialog, (newShow,oldShow) => { 
        if ((newShow) && (newShow !== oldShow) && (!formData.medRetrieved)) { 
            getWeblink();
        }
    });

    const emit=defineEmits(["updateModalWeblink"]);   
    const webLinkHeight = computed<string>(() => (props.displayHeight - 55) + 'px'); 

    async function getWeblink() : Promise<void> {   
        const medInputs : IMedInformationInput = {InformationType: medInformationType.WebLinks, PatientID: props.patientID, PrescriptionID: props.prescriptionID, MedID: props.medicationID};
        const respGet = await GetMedWeblink(medInputs);  
        if (!respGet.success) {  
            formData.medErrorMessage = respGet.error?.error ?? errorReturnMessage.weblinks; 
            emit('updateModalWeblink',false,"");  
            return;
        } 
        formData.noData = (respGet.success.weblinkList.length === 0 ? true : false);
        formData.medRetrieved = true; 
        formData.weblinkList = respGet.success.weblinkList; 
        
        if (formData.weblinkList.length > 0) { 
            const report_data = 
                `<div style="${printClass.recordTitle}">Web Links</div>` +
                "<ol>" + formData.weblinkList.map(item => "<li>" + item.description + " - " + item.url + "</li>").join("") + "</ol>";
            emit('updateModalWeblink',true,report_data);  
        }
    } 
    function openLink(link : string)  : void {
        window.open(link,"","help=no ,status=no,toolbar=no,menubar=no,scrollbars=no,resizable=yes,width=600,height=600,top=176,left=470,location=no");
    }
    async function addWebLink() : Promise<void> {
        resetNewWebLink();
        const web_guideline_html =
            `<div class="text-isalus-primary" style="font-size: 1rem; padding-bottom: 1rem;">Enter the new Weblink Guideline information below:</div>` +
            `<div style="font-size: .75rem">Description:&nbsp;</div>` +
            `<input style="font-size: .75rem; width: 75%;" type="text" maxlength="100" onchange="newWebLink(0,this.value)">` +
            `<br/>` +
            `<div style="font-size: .75rem; padding-top: .75rem;">URL:&nbsp; (must include https://)</div>` +
            `<input style="font-size: .75rem; width: 75%;" type="text" maxlength="200" onchange="newWebLink(1,this.value)" value="https://">`;
        const result = await confirmDialog(web_guideline_html, { titleHTML: `Add New Web Link Guideline`,RejectText: "Cancel", ConfirmText: "Save" });  
        if (!result) { 
            return;
        }
        const web_information = getNewWebLink();
        web_information[0] = removeBadChars(web_information[0],'"<>~|');
        web_information[1] = removeBadChars(web_information[1],'"<>~|');
        if (!web_information[0]) {
            popAlert("Invalid Weblink Guideline Description.");
            return;
        }         
        else if (!isValidUrl(web_information[1])) {
            popAlert("Invalid Weblink Guideline URL.");
            return;
        }
        const webLinkInputs : IWebLinkInputs = {PrescriptionID: props.prescriptionID, MedID: props.medicationID, Url: web_information[1], UrlDescription: web_information[0]};
        const respSave = await UpdateWebLink(webLinkInputs);

        if (respSave.error) {  
            popAlert(respSave.error.error ?? "Unable to add Weblink Guideline");
            return;
        }
        if (respSave.success)  {
            if (respSave.success.errorCode !== 0)  {
                popAlert(respSave.success.errorMessage ?? "Unable to add Weblink Guideline");
                return;
            }
            formData.weblinkList?.push({url: web_information[1], description: web_information[0], creationData: "Created by you today."}); 
            formData.noData = false;
        }  
    }  
    function resetNewWebLink() {
        new_web_link = ["",""]; // value located on razor page ...
    } 
    function getNewWebLink() {
        return new_web_link ?? ["",""]; // value located on razor page ...
    }
</script>

<template> 
    <div class="row pt-2"> 
        <div v-if="formData.medErrorMessage" class="col text-danger"> 
            {{ formData.medErrorMessage }}
        </div>
        <div v-if="!formData.medErrorMessage" class="col"> 
            <div class="row"> 
                <div class="fw-bold fs-6 text-isalus-primary pb-2 ">
                   Web Links
                </div>
            </div>
            <div class="row"> 
                <div class="col skinnyscroll " :style="{'overflow-y': 'scroll', 'overflow-x': 'hidden', 'height': webLinkHeight}">  
                    <div v-if="formData.noData" class="text-muted"> 
                        {{ no_drug_information }}
                    </div>
                    <div v-if="!formData.noData" v-for="(weblinkRecord,index) in formData.weblinkList?.filter(item => item.url !== '')"> 
                        <div> 
                            {{(index + 1) }}. <a @click="openLink(weblinkRecord.url)" style="cursor: pointer;" class="text-decoration-underline">{{weblinkRecord.description}}</a>                            
                            <span class="ps-1">- {{weblinkRecord.url}}</span>
                            <div class="ps-2 text-muted">{{weblinkRecord.creationData}}</div>
                        </div>
                    </div> 
                </div>
            </div>
            <div class="row"> 
                <div class="col text-end"> 
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Add"  
                        :text="'Add'"  
                        :tooltip="'Add a new Web Link'"  
                        :size="ButtonDisplaySize.Small" 
                        :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                        :performAction="addWebLink" 
                        :disabled="false" />
                </div>
            </div>
        </div>
    </div>
</template>